'use client';

import { useState } from 'react';
import ICaseStudiesPage, { ICaseStudyResponse } from '@/models/caseStudy';
import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import Link from 'next/link';
import ContactCTA from '@/components/ContactCTA';
import Content from '@/components/Content';
import Pagination from '@/components/Pagination';
import { Markdown } from '@/lib/markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FrequentlyAskedQuestions from '@/components/FrequentlyAskedQuestions';
import { Shapes } from '@/components/Shapes';

export default function CaseStudiesPage(props: {
	caseStudyPage: ICaseStudiesPage;
	caseStudies: ICaseStudyResponse;
	featuredCaseStudy: ICaseStudyResponse;
}) {
	'use client';

	const [pageNum, setPageNum] = useState(0);
	const perPage = 8;

	const caseStudyPage = props.caseStudyPage;

	if (!caseStudyPage) {
		return null;
	}

	const { caseStudies } = props;
	const paginatedCaseStudies = caseStudies?.caseStudies?.slice(
		pageNum * perPage,
		(pageNum + 1) * perPage
	);

	return (
		<section className="relative ">
			<div className="z-20 relative bg-contain xl:bg-cover bg-white text-white bg-bottom flex flex-col gap-8 md:gap-28 ">
				<div className="bg-primaryBg py-12 md:h-[600px] flex items-center">
					<Content
						priority
						h1
						description={caseStudyPage.header?.contentDescription}
						className="text-white "
						title={caseStudyPage.header?.title}
						caption={
							<div className="flex gap-4 text-white items-center mb-6 flex-wrap">
								<Link className="hover:text-primary-100" href="/">
									Home
								</Link>

								<FontAwesomeIcon size="sm" icon={['far', 'chevron-right']} />

								<p className="text-primary-100">{caseStudyPage.header?.title}</p>
							</div>
						}
						image={caseStudyPage.header?.image}
						buttons={caseStudyPage.header?.buttonsCollection?.items}
						iconList={caseStudyPage.header?.iconListCollection?.items}
						imageCover={caseStudyPage.header?.imageCover}
						reverse={caseStudyPage.header?.reverse}
					/>
				</div>
			</div>

			<div className="bg-white z-20 relative black py-12 md:py-32 flex flex-col">
				<Shapes
					shape="corner-blob"
					className="absolute -left-6 -bottom-[200px] w-[100px] md:w-[120px]"
				/>
				<Shapes
					shape="right-blob"
					className="absolute -right-20 md:-right-6 top-32 md:top-[400px] w-[190px] md:w-[220px]"
				/>
				<div className="container px-4 mb-8 z-20 relative">
					{!!caseStudyPage.allCaseStudiesTitle?.length && (
						<h2 className="text-2xl md:text-3xl font-bold  mb-4 md:mb-8">
							{caseStudyPage.allCaseStudiesTitle}
						</h2>
					)}
					<div className="flex gap-4 flex-col mb-4 text-base md:text-lg max-w-2xl">
						{!!caseStudyPage.allCaseStudiesDescription && (
							<Markdown content={caseStudyPage.allCaseStudiesDescription} />
						)}
					</div>
				</div>

				<div className="flex flex-col gap-6 md:gap-12 overflow-hidden z-20 relative container">
					{paginatedCaseStudies?.map((caseStudy) => {
						const formattedIndex =
							pageNum * perPage + caseStudies.caseStudies.indexOf(caseStudy) + 1;
						const displayIndex =
							formattedIndex > 9 ? formattedIndex : `0${formattedIndex}`;
						const reverse = (caseStudies.caseStudies.indexOf(caseStudy) + 1) % 2 === 0;
						return (
							<Content
								disableContainer
								className={`bg-card  ${
									reverse
										? 'pl-0 pr-0 md:pr-8 rounded-xl'
										: 'pl-0 md:pl-8 pr-0 rounded-xl'
								}`}
								key={caseStudy.slug}
								description={caseStudy.excerpt}
								title={caseStudy?.title}
								caption={String(displayIndex)}
								image={caseStudy?.cardImage}
								buttons={[
									{
										link: `/case-studies/${caseStudy.slug}`,
										text: 'Discover more',
										color: 'primary',
									},
								]}
								imageCover="cover"
								reverse={reverse}
							/>
						);
					})}
				</div>
				<div className="pt-8 md:pt-16 bg-white">
					<Pagination
						defaultPerPage={perPage}
						currentPage={pageNum}
						totalItems={props.caseStudies?.meta?.total ?? 0}
						onPageChange={(page) => {
							setPageNum(page);
							window.scrollTo({ top: 0, behavior: 'smooth' });
						}}
					/>
				</div>
			</div>

			<ContactCTA
				title={caseStudyPage.callToAction?.title}
				description={
					caseStudyPage.callToAction?.contentDescription && (
						<Markdown content={caseStudyPage.callToAction.contentDescription} />
					)
				}
				buttons={caseStudyPage.callToAction?.buttonsCollection?.items}
			/>

			<div className="z-10 relative py-12  md:py-32 flex flex-col overflow-clip bg-white">
				<FrequentlyAskedQuestions
					faqs={caseStudyPage?.faqsCollection?.items}
					title={caseStudyPage?.faqsTitle ?? 'FAQs'}
					description={caseStudyPage.faqsDescription}
				/>
			</div>
		</section>
	);
}
